/**
 * Firebase Services - Real Production Data
 * Gestisce Firestore, Storage e Auth per NexScribe
 */

import {
  collection,
  doc,
  addDoc,
  getDoc,
  getDocs,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  onSnapshot,
  serverTimestamp,
} from 'firebase/firestore'
import {
  ref,
  uploadBytesResumable,
  getDownloadURL,
  deleteObject,
} from 'firebase/storage'
import {
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  GoogleAuthProvider,
  signInWithPopup,
  User as FirebaseUser,
} from 'firebase/auth'
import { db, storage, auth, functions } from '@/lib/firebase'
import { httpsCallable } from 'firebase/functions'
import type { Recording, VoiceProfile, User } from '@/store/auth'

// ============================================
// AUTH SERVICE
// ============================================

export const authService = {
  // Get current user
  getCurrentUser: (): FirebaseUser | null => {
    return auth.currentUser
  },

  // Listen to auth state changes
  onAuthStateChange: (callback: (user: FirebaseUser | null) => void) => {
    return onAuthStateChanged(auth, callback)
  },

  // Email/Password Login
  loginWithEmail: async (email: string, password: string) => {
    const result = await signInWithEmailAndPassword(auth, email, password)
    return result.user
  },

  // Email/Password Register
  registerWithEmail: async (email: string, password: string, name: string) => {
    const result = await createUserWithEmailAndPassword(auth, email, password)

    // Create user document in Firestore
    await addDoc(collection(db, 'users'), {
      uid: result.user.uid,
      email: result.user.email,
      name: name,
      plan: 'free',
      minutesUsed: 0,
      minutesLimit: 60,
      voiceIdsUsed: 0,
      voiceIdsLimit: 3,
      createdAt: serverTimestamp(),
    })

    return result.user
  },

  // Google Login
  loginWithGoogle: async () => {
    const provider = new GoogleAuthProvider()
    const result = await signInWithPopup(auth, provider)

    // Check if user document exists, if not create it
    const usersRef = collection(db, 'users')
    const q = query(usersRef, where('uid', '==', result.user.uid))
    const snapshot = await getDocs(q)

    if (snapshot.empty) {
      await addDoc(collection(db, 'users'), {
        uid: result.user.uid,
        email: result.user.email,
        name: result.user.displayName || 'Utente',
        avatar: result.user.photoURL,
        plan: 'free',
        minutesUsed: 0,
        minutesLimit: 60,
        voiceIdsUsed: 0,
        voiceIdsLimit: 3,
        createdAt: serverTimestamp(),
      })
    }

    return result.user
  },

  // Logout
  logout: async () => {
    await signOut(auth)
  },

  // Get user profile from Firestore
  getUserProfile: async (uid: string): Promise<User | null> => {
    const usersRef = collection(db, 'users')
    const q = query(usersRef, where('uid', '==', uid))
    const snapshot = await getDocs(q)

    if (snapshot.empty) return null

    const data = snapshot.docs[0].data()
    return {
      id: snapshot.docs[0].id,
      email: data.email,
      name: data.name,
      avatar: data.avatar,
      plan: data.plan,
      minutesUsed: data.minutesUsed,
      minutesLimit: data.minutesLimit,
      voiceIdsUsed: data.voiceIdsUsed,
      voiceIdsLimit: data.voiceIdsLimit,
      createdAt: data.createdAt?.toDate?.()?.toISOString() || new Date().toISOString(),
    }
  },
}

// ============================================
// RECORDINGS SERVICE
// ============================================

export const recordingsService = {
  // Get all recordings for current user
  getAll: async (userId: string): Promise<Recording[]> => {
    const recordingsRef = collection(db, 'recordings')
    const q = query(
      recordingsRef,
      where('userId', '==', userId),
      orderBy('createdAt', 'desc')
    )
    const snapshot = await getDocs(q)

    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      createdAt: doc.data().createdAt?.toDate?.()?.toISOString() || new Date().toISOString(),
      updatedAt: doc.data().updatedAt?.toDate?.()?.toISOString() || new Date().toISOString(),
    })) as Recording[]
  },

  // Get single recording
  get: async (id: string): Promise<Recording | null> => {
    const docRef = doc(db, 'recordings', id)
    const snapshot = await getDoc(docRef)

    if (!snapshot.exists()) return null

    const data = snapshot.data()
    return {
      id: snapshot.id,
      ...data,
      createdAt: data.createdAt?.toDate?.()?.toISOString() || new Date().toISOString(),
      updatedAt: data.updatedAt?.toDate?.()?.toISOString() || new Date().toISOString(),
    } as Recording
  },

  // Create new recording
  create: async (userId: string, data: Partial<Recording>): Promise<string> => {
    const docRef = await addDoc(collection(db, 'recordings'), {
      ...data,
      userId,
      status: 'uploading',
      progress: 0,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    })
    return docRef.id
  },

  // Update recording
  update: async (id: string, data: Partial<Recording>): Promise<void> => {
    const docRef = doc(db, 'recordings', id)
    await updateDoc(docRef, {
      ...data,
      updatedAt: serverTimestamp(),
    })
  },

  // Delete recording
  delete: async (id: string, audioUrl?: string): Promise<void> => {
    // Delete from Firestore
    await deleteDoc(doc(db, 'recordings', id))

    // Delete audio file from Storage if exists
    if (audioUrl) {
      try {
        const storageRef = ref(storage, audioUrl)
        await deleteObject(storageRef)
      } catch (e) {
        console.warn('Could not delete audio file:', e)
      }
    }
  },

  // Upload audio file
  uploadAudio: async (
    userId: string,
    recordingId: string,
    file: File,
    onProgress?: (progress: number) => void
  ): Promise<string> => {
    const path = `audio/${userId}/${recordingId}/${file.name}`
    const storageRef = ref(storage, path)

    return new Promise((resolve, reject) => {
      const uploadTask = uploadBytesResumable(storageRef, file)

      uploadTask.on(
        'state_changed',
        (snapshot) => {
          const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100
          onProgress?.(Math.round(progress))
        },
        (error) => reject(error),
        async () => {
          const downloadURL = await getDownloadURL(uploadTask.snapshot.ref)
          resolve(downloadURL)
        }
      )
    })
  },

  // Listen to recording changes (real-time)
  subscribe: (id: string, callback: (recording: Recording | null) => void) => {
    const docRef = doc(db, 'recordings', id)
    return onSnapshot(docRef, (snapshot) => {
      if (!snapshot.exists()) {
        callback(null)
        return
      }
      const data = snapshot.data()
      callback({
        id: snapshot.id,
        ...data,
        createdAt: data.createdAt?.toDate?.()?.toISOString() || new Date().toISOString(),
        updatedAt: data.updatedAt?.toDate?.()?.toISOString() || new Date().toISOString(),
      } as Recording)
    })
  },

  // Listen to all recordings (real-time)
  subscribeAll: (userId: string, callback: (recordings: Recording[]) => void) => {
    const recordingsRef = collection(db, 'recordings')
    const q = query(
      recordingsRef,
      where('userId', '==', userId),
      orderBy('createdAt', 'desc')
    )

    return onSnapshot(q, (snapshot) => {
      const recordings = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate?.()?.toISOString() || new Date().toISOString(),
        updatedAt: doc.data().updatedAt?.toDate?.()?.toISOString() || new Date().toISOString(),
      })) as Recording[]
      callback(recordings)
    })
  },
}

// ============================================
// VOICE PROFILES SERVICE
// ============================================

export const voiceProfilesService = {
  // Get all voice profiles for current user
  getAll: async (userId: string): Promise<VoiceProfile[]> => {
    const profilesRef = collection(db, 'voiceProfiles')
    const q = query(
      profilesRef,
      where('userId', '==', userId),
      orderBy('createdAt', 'desc')
    )
    const snapshot = await getDocs(q)

    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      createdAt: doc.data().createdAt?.toDate?.()?.toISOString() || new Date().toISOString(),
      lastSeen: doc.data().lastSeen?.toDate?.()?.toISOString() || new Date().toISOString(),
    })) as VoiceProfile[]
  },

  // Get single profile
  get: async (id: string): Promise<VoiceProfile | null> => {
    const docRef = doc(db, 'voiceProfiles', id)
    const snapshot = await getDoc(docRef)

    if (!snapshot.exists()) return null

    const data = snapshot.data()
    return {
      id: snapshot.id,
      ...data,
      createdAt: data.createdAt?.toDate?.()?.toISOString() || new Date().toISOString(),
      lastSeen: data.lastSeen?.toDate?.()?.toISOString() || new Date().toISOString(),
    } as VoiceProfile
  },

  // Create new profile
  create: async (userId: string, data: { name: string; role?: string; email?: string; company?: string }): Promise<string> => {
    const docRef = await addDoc(collection(db, 'voiceProfiles'), {
      ...data,
      userId,
      sampleCount: 0,
      isVerified: false,
      status: 'Pending',
      confidence: '0%',
      createdAt: serverTimestamp(),
      lastSeen: serverTimestamp(),
    })
    return docRef.id
  },

  // Update profile
  update: async (id: string, data: Partial<VoiceProfile>): Promise<void> => {
    const docRef = doc(db, 'voiceProfiles', id)
    await updateDoc(docRef, {
      ...data,
      lastSeen: serverTimestamp(),
    })
  },

  // Delete profile
  delete: async (id: string): Promise<void> => {
    await deleteDoc(doc(db, 'voiceProfiles', id))
  },

  // Upload voice sample
  uploadSample: async (
    userId: string,
    profileId: string,
    file: File,
    onProgress?: (progress: number) => void
  ): Promise<string> => {
    const path = `voice-samples/${userId}/${profileId}/${Date.now()}_${file.name}`
    const storageRef = ref(storage, path)

    return new Promise((resolve, reject) => {
      const uploadTask = uploadBytesResumable(storageRef, file)

      uploadTask.on(
        'state_changed',
        (snapshot) => {
          const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100
          onProgress?.(Math.round(progress))
        },
        (error) => reject(error),
        async () => {
          const downloadURL = await getDownloadURL(uploadTask.snapshot.ref)

          // Update sample count
          const profileRef = doc(db, 'voiceProfiles', profileId)
          const profileSnap = await getDoc(profileRef)
          if (profileSnap.exists()) {
            await updateDoc(profileRef, {
              sampleCount: (profileSnap.data().sampleCount || 0) + 1,
              lastSeen: serverTimestamp(),
            })
          }

          resolve(downloadURL)
        }
      )
    })
  },

  // Train model via Cloud Function
  train: async (id: string): Promise<{ success: boolean; embeddingsExtracted?: number; confidence?: number }> => {
    try {
      const trainVoiceProfile = httpsCallable<{ profileId: string }, { success: boolean; embeddingsExtracted: number; totalSamples: number; confidence: number }>(
        functions,
        'trainVoiceProfile'
      )

      const result = await trainVoiceProfile({ profileId: id })
      return result.data
    } catch (error: any) {
      // If Cloud Function fails, try fallback to just update status
      console.error('Cloud Function error:', error)

      // Check for specific error codes
      if (error.code === 'failed-precondition') {
        throw new Error('Servono almeno 3 campioni audio per il training')
      }

      throw error
    }
  },

  // Subscribe to all profiles (real-time)
  subscribeAll: (userId: string, callback: (profiles: VoiceProfile[]) => void) => {
    const profilesRef = collection(db, 'voiceProfiles')
    const q = query(
      profilesRef,
      where('userId', '==', userId),
      orderBy('createdAt', 'desc')
    )

    return onSnapshot(q, (snapshot) => {
      const profiles = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate?.()?.toISOString() || new Date().toISOString(),
        lastSeen: doc.data().lastSeen?.toDate?.()?.toISOString() || new Date().toISOString(),
      })) as VoiceProfile[]
      callback(profiles)
    })
  },
}

// ============================================
// NOTIFICATIONS SERVICE
// ============================================

export interface Notification {
  id: string
  userId: string
  type: 'success' | 'warning' | 'info' | 'error'
  title: string
  message: string
  read: boolean
  createdAt: string
}

export const notificationsService = {
  // Get all notifications for user
  getAll: async (userId: string): Promise<Notification[]> => {
    const notificationsRef = collection(db, 'notifications')
    const q = query(
      notificationsRef,
      where('userId', '==', userId),
      orderBy('createdAt', 'desc')
    )
    const snapshot = await getDocs(q)

    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      createdAt: doc.data().createdAt?.toDate?.()?.toISOString() || new Date().toISOString(),
    })) as Notification[]
  },

  // Create notification
  create: async (userId: string, data: Omit<Notification, 'id' | 'userId' | 'createdAt'>): Promise<string> => {
    const docRef = await addDoc(collection(db, 'notifications'), {
      ...data,
      userId,
      createdAt: serverTimestamp(),
    })
    return docRef.id
  },

  // Mark as read
  markAsRead: async (id: string): Promise<void> => {
    await updateDoc(doc(db, 'notifications', id), { read: true })
  },

  // Mark all as read
  markAllAsRead: async (userId: string): Promise<void> => {
    const notificationsRef = collection(db, 'notifications')
    const q = query(
      notificationsRef,
      where('userId', '==', userId),
      where('read', '==', false)
    )
    const snapshot = await getDocs(q)

    const updates = snapshot.docs.map(doc =>
      updateDoc(doc.ref, { read: true })
    )
    await Promise.all(updates)
  },

  // Subscribe to notifications (real-time)
  subscribe: (userId: string, callback: (notifications: Notification[]) => void) => {
    const notificationsRef = collection(db, 'notifications')
    const q = query(
      notificationsRef,
      where('userId', '==', userId),
      orderBy('createdAt', 'desc')
    )

    return onSnapshot(q, (snapshot) => {
      const notifications = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate?.()?.toISOString() || new Date().toISOString(),
      })) as Notification[]
      callback(notifications)
    })
  },
}
